//TODO: delete this file after new pricing migration
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';

// Libs
import { TRIAL, NO_ACTIVE_PLAN } from '@/lib/plans';
import { CHANGE_PAYMENT_METHOD } from '@/lib/settings/subscriptionInvoices/steps';

// Hooks
import useToggle from '@/utils/hooks/utils/useToggle';
import useRouterQuery from '@/utils/hooks/utils/useRouterQuery';
import useAcl from '@/hooks/user/useAcl';
import useFormatNumber from '@/hooks/format/useFormatNumber';
import useUser from '@/utils/hooks/swr/useUser';
import useBillingSubscription from '@/utils/hooks/swr/useBillingSubscription';
// Utils
import { BILLING } from '@/lib/acl/aclRightsList';

// Components
import { ChangePaymentModal } from '@/components/pages/settings/subscription-invoices/UserBilling/ChangePaymentModal';
import Button from '@/components/base/Button';
import { LoaderText } from '@/components/pages/settings/subscription-invoices/PageContent/PageLoading';

const OldBillingWrapper = () => {
    const { isLoading } = useBillingSubscription();
    if (isLoading) {
        return <LoaderText />;
    }

    return <OldBilling />;
};

const OldBilling = () => {
    const { t } = useTranslation('settings');
    const { hasAcl } = useAcl();
    const router = useRouter();
    const { step } = router.query;
    const { user } = useUser({
        revalidateIfStale: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    const { plan } = useBillingSubscription();

    const userNumberFormat = user?.settings?.numbers_format;
    const { formatPriceCurrencySymbol } = useFormatNumber(userNumberFormat);

    const hasBilling = plan?.product_key !== NO_ACTIVE_PLAN && plan?.product_key !== TRIAL;

    const { isOpen: isChangePaymentModalOpen, toggle: toggleChangePaymentModal } = useToggle({
        initial: Boolean(hasBilling && step === CHANGE_PAYMENT_METHOD && hasAcl(BILLING.update))
    });

    const { deleteQuery } = useRouterQuery();
    useEffect(() => {
        if (step === CHANGE_PAYMENT_METHOD) {
            if (!isChangePaymentModalOpen && hasAcl(BILLING.update)) {
                toggleChangePaymentModal();
            }
            router.replace(deleteQuery('step'));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step]);

    if (!hasBilling) {
        return (
            <p data-testid="no-billing-information">{t('subscription_invoices_page.no_billing')}</p>
        );
    }

    return (
        <>
            {!plan?.is_manual ? (
                <>
                    <p
                        className="mb-3 flex justify-between text-sm text-icoGray-500"
                        data-testid="billing-manual-plan">
                        <span>
                            {t('subscription_invoices_page.plan', {
                                plan: t(`subscription_invoices_page.${plan.product_key}`)
                            })}
                        </span>
                        <span>{formatPriceCurrencySymbol(plan?.plan_price, plan?.currency)}</span>
                    </p>
                    <p className="mb-3 flex justify-between text-sm text-icoGray-500">
                        <span>{t('subscription_invoices_page.add_ons')}</span>
                        <span>
                            {formatPriceCurrencySymbol(plan?.options_price ?? 0, plan?.currency)}
                        </span>
                    </p>
                </>
            ) : null}
            <p className="mb-8 flex justify-between text-sm" data-testid="addons">
                <span>{t('subscription_invoices_page.plan_with_add_ons')}</span>
                <span className="text-icoBlue">
                    {formatPriceCurrencySymbol(plan?.total, plan?.currency)}
                </span>
            </p>
            {plan.update_payment_method_url ? (
                <>
                    <Button
                        className="m-auto mb-2 w-full"
                        variant="secondary"
                        onClick={toggleChangePaymentModal}
                        data-testid="button-change-payment-method">
                        {t('subscription_invoices_page.change_payment_method')}
                    </Button>
                    <ChangePaymentModal
                        isOpen={isChangePaymentModalOpen}
                        closeModal={toggleChangePaymentModal}
                    />
                </>
            ) : null}

            {plan.update_billing_address_url ? (
                <div className="w-full text-center">
                    <Link
                        data-testid="link-change-billing-address"
                        className="text-xs text-icoBlue underline"
                        href={plan?.update_billing_address_url}
                        target="_blank"
                        rel="noopener noreferrer">
                        {t('subscription_invoices_page.change_billing_address')}
                    </Link>
                </div>
            ) : null}
        </>
    );
};

export default OldBillingWrapper;
